import { Component } from '@theme/component'
import {
  fetchConfig,
  debounce,
  onAnimationEnd,
  prefersReducedMotion,
  resetShimmer
} from '@theme/utilities'
import { morphSection, sectionRenderer } from '@theme/section-renderer'
import { ThemeEvents } from '@theme/events'
import { cartPerformance } from '@theme/performance'
import { showFetchError } from '@theme/fetch-error'
import { refreshCartSurfaces } from '@theme/cart-add'
import {
  createViewEventElement,
  CartErrorEvent,
  CartLinesUpdateEvent,
  StandardEvents
} from '@shopify/events'

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * True when the page came back from the back/forward cache or was rebuilt from the
 * HTTP cache by a Back/Forward navigation; either way its cart markup may be stale.
 * @param {PageTransitionEvent} event
 * @returns {boolean}
 */
export function isHistoryRestore(event) {
  if (event.persisted) return true
  const [navigation] = performance.getEntriesByType?.('navigation') ?? []
  return (
    /** @type {{ type?: string } | undefined} */ (navigation)?.type ===
    'back_forward'
  )
}

/**
 * Matches the `data-cart-signature` rendered on each `cart-items-component`.
 * @param {{ item_count?: number, total_price?: number }} cart - `/cart.js` payload.
 * @returns {string}
 */
export function cartSignature(cart) {
  return `${cart.item_count ?? 0}:${cart.total_price ?? 0}`
}

let historyRestoreInFlight = false

/**
 * Re-renders cart surfaces after a history restore, only when the live cart differs from
 * the rendered one. Dispatches no CartLinesUpdateEvent, so the drawer never auto-opens.
 * @param {PageTransitionEvent} event
 * @returns {Promise<boolean>} True when surfaces were refreshed.
 */
export async function refreshCartAfterHistoryRestore(event) {
  if (historyRestoreInFlight || !isHistoryRestore(event)) return false

  const renderedSignatures = Array.from(
    document.querySelectorAll('cart-items-component[data-cart-signature]'),
    (node) => /** @type {HTMLElement} */ (node).dataset.cartSignature
  )
  if (renderedSignatures.length === 0) return false

  historyRestoreInFlight = true
  try {
    const response = await fetch('/cart.js', { cache: 'no-store' })
    if (!response.ok) return false
    const cart = await response.json()
    const liveSignature = cartSignature(cart)
    if (renderedSignatures.every((signature) => signature === liveSignature)) {
      return false
    }

    await refreshCartSurfaces({ cart })
    for (const selector of document.querySelectorAll(
      'cart-quantity-selector-component'
    )) {
      /** @type {any} */ selector.updateButtonStates?.()
    }
    return true
  } catch (error) {
    if (/** @type {Error} */ (error)?.name !== 'AbortError') {
      console.warn('[cart-items] History restore refresh failed:', error)
    }
    return false
  } finally {
    historyRestoreInFlight = false
  }
}

window.addEventListener('pageshow', refreshCartAfterHistoryRestore)

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends createViewEventElement(Component) {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this)

  /**
   * @param {Event} event
   */
  #isExternalCartUpdate(event) {
    return !(event.target instanceof Node) || !this.contains(event.target)
  }

  /** @param {import('@shopify/events').CartDiscountUpdateEvent} event */
  #handleDiscountUpdate = (event) => {
    const external = this.#isExternalCartUpdate(event)
    event.promise
      ?.then(({ detail }) => {
        // cart-discount.js morphs its own section, so an internal update only needs button states.
        if (!external) {
          this.#updateCartQuantitySelectorButtonStates()
          return
        }

        const sectionsHtml = detail?.sections?.[this.sectionId]
        if (sectionsHtml) {
          morphSection(this.sectionId, sectionsHtml)
          this.#updateCartQuantitySelectorButtonStates()
        } else {
          sectionRenderer.renderSection(this.sectionId, { cache: false })
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') {
          console.warn('[cart-items] Event promise rejected:', error)
        }
      })
  }

  /** @param {import('@shopify/events').CartNoteUpdateEvent} event */
  #handleNoteUpdate = (event) => {
    if (!this.#isExternalCartUpdate(event)) return
    event.promise
      ?.then(({ detail }) => {
        const sections = /** @type {Record<string, string> | undefined} */ (
          detail?.sections
        )
        const sectionsHtml = sections?.[this.sectionId]
        if (sectionsHtml) {
          morphSection(this.sectionId, sectionsHtml)
        } else {
          sectionRenderer.renderSection(this.sectionId, { cache: false })
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') {
          console.warn('[cart-items] Event promise rejected:', error)
        }
      })
  }

  connectedCallback() {
    super.connectedCallback()

    document.addEventListener(
      StandardEvents.cartLinesUpdate,
      this.#handleCartUpdate
    )
    document.addEventListener(
      StandardEvents.cartDiscountUpdate,
      this.#handleDiscountUpdate
    )
    document.addEventListener(
      StandardEvents.cartNoteUpdate,
      this.#handleNoteUpdate
    )
    document.addEventListener(
      ThemeEvents.quantitySelectorUpdate,
      this.#debouncedOnChange
    )
  }

  disconnectedCallback() {
    super.disconnectedCallback()

    document.removeEventListener(
      StandardEvents.cartLinesUpdate,
      this.#handleCartUpdate
    )
    document.removeEventListener(
      StandardEvents.cartDiscountUpdate,
      this.#handleDiscountUpdate
    )
    document.removeEventListener(
      StandardEvents.cartNoteUpdate,
      this.#handleNoteUpdate
    )
    document.removeEventListener(
      ThemeEvents.quantitySelectorUpdate,
      this.#debouncedOnChange
    )
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {import('@theme/events').QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    const { quantity, cartLine: line } = event.detail

    if (!line) return

    if (quantity === 0) {
      return this.onLineItemRemove(line)
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change'
    })
    const lineItemRow = this.refs.cartItemRows[line - 1]

    if (!lineItemRow) return

    const textComponent = /** @type {TextComponent | undefined} */ (
      lineItemRow.querySelector('text-component')
    )
    textComponent?.shimmer()
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear'
    })

    const cartItemRowToRemove = this.refs.cartItemRows[line - 1]

    if (!cartItemRowToRemove) return

    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...this.refs.cartItemRows.filter(
        (row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key
      )
    ]

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      let removed = false
      const remove = () => {
        if (removed) return
        removed = true
        row.remove()
      }

      if (prefersReducedMotion()) return remove()

      row.style.setProperty('--row-height', `${row.clientHeight}px`)
      row.classList.add('removing')

      // Race the animation against a hard timeout so a row never gets stuck
      // in `removing` if the animation is canceled (e.g. morph re-keys the
      // row mid-animation, or CSS fails to apply).
      onAnimationEnd(row, remove)
      setTimeout(remove, 1000)
    })
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(
      `${config.action}:user-action`
    )

    this.#disableCartItems()

    const { line, quantity } = config
    const { cartTotal } = this.refs

    const cartItemsComponents = document.querySelectorAll(
      'cart-items-component'
    )
    const sectionsToUpdate = new Set([this.sectionId])
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId)
      }
    })

    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname
    })

    cartTotal?.shimmer()

    const deferredUpdatePromise = CartLinesUpdateEvent.createPromise()
    const lineId = this.refs.cartItemRows[line - 1]?.dataset.key ?? ''
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action:
          config.action === 'change' && quantity > 0 ? 'update' : 'remove',
        context: 'cart',
        lines: [{ id: lineId, quantity }],
        promise: deferredUpdatePromise.promise
      })
    )

    fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => response.text())
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText)

        resetShimmer(this)

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText)
          deferredUpdatePromise.resolve({
            detail: {
              didError: true,
              source: 'cart-items-component',
              sourceId: this.id
            }
          })
          return
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        )

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector(
          '[ref="cartItemCount"]'
        )?.textContent
        const newCartItemCount = newCartHiddenItemCount
          ? parseInt(newCartHiddenItemCount, 10)
          : 0

        // Update data-cart-quantity for all matching variants
        this.#updateQuantitySelectors(parsedResponseText)

        // Morph FIRST so listeners see up-to-date DOM when promise resolves.
        morphSection(
          this.sectionId,
          parsedResponseText.sections[this.sectionId]
        )

        deferredUpdatePromise.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(
            parsedResponseText
          ),
          detail: {
            sections: parsedResponseText.sections,
            items: parsedResponseText.items,
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            didError: false
          }
        })

        this.#updateCartQuantitySelectorButtonStates()
      })
      .catch((error) => {
        deferredUpdatePromise.resolve({
          detail: {
            didError: true,
            source: 'cart-items-component',
            sourceId: this.id
          }
        })

        this.#handleNetworkError(line, error)
        this.dispatchEvent(
          new CartErrorEvent({
            error: error?.message || 'Failed to update cart',
            code: 'SERVICE_UNAVAILABLE'
          })
        )
        console.error(error)
      })
      .finally(() => {
        this.#enableCartItems()
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker)
      })
  }

  /**
   * Surfaces an accessible error when a quantity change fails at the network
   * level, and restores the line's quantity input to its last known value so
   * the UI is not left displaying an unsaved quantity.
   *
   * @param {number} line - The cart line index (1-based).
   * @param {unknown} error - The originating error.
   */
  #handleNetworkError(line, error) {
    const quantitySelector = this.refs.quantitySelectors[line - 1]
    const quantityInput = quantitySelector?.querySelector('input')

    if (quantityInput instanceof HTMLInputElement) {
      quantityInput.value = quantityInput.defaultValue
    }

    const cartItemError = this.refs[`cartItemError-${line}`]
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`]

    showFetchError({
      container:
        cartItemErrorContainer instanceof HTMLElement
          ? cartItemErrorContainer
          : null,
      textTarget: cartItemError instanceof HTMLElement ? cartItemError : null,
      error
    })
  }

  /**
   * Handles the cart update.
   *
   * @param {CartLinesUpdateEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event.target === this) return

    event.promise
      ?.then(async ({ detail }) => {
        const sectionId = this.sectionId
        const cartItemsHtml = detail?.sections?.[sectionId]

        if (detail?.surfacesRefreshed && cartItemsHtml) {
          this.#updateCartQuantitySelectorButtonStates()
          return
        }

        try {
          if (cartItemsHtml) {
            await morphSection(sectionId, cartItemsHtml)
          } else {
            await sectionRenderer.renderSection(sectionId, { cache: false })
          }
        } catch (error) {
          console.warn('[cart-items] Section refresh failed, retrying', error)
          await sectionRenderer
            .renderSection(sectionId, { cache: false })
            .catch(() => {})
        }

        this.#updateCartQuantitySelectorButtonStates()
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') {
          console.warn('[cart-items] Event promise rejected:', error)
        }
      })
  }

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1]
    const quantityInput = quantitySelector?.querySelector('input')

    if (!quantityInput) throw new Error('Quantity input not found')

    quantityInput.value = quantityInput.defaultValue

    const cartItemError = this.refs[`cartItemError-${line}`]
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`]

    if (!(cartItemError instanceof HTMLElement))
      throw new Error('Cart item error not found')
    if (!(cartItemErrorContainer instanceof HTMLElement))
      throw new Error('Cart item error container not found')

    cartItemError.textContent = parsedResponseText.errors
    cartItemErrorContainer.classList.remove('hidden')
  }

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled')
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled')
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString()
      const selectors = document.querySelectorAll(
        `quantity-selector-component[data-variant-id="${variantId}"], cart-quantity-selector-component[data-variant-id="${variantId}"]`
      )

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]')
        if (!input) continue

        input.setAttribute('data-cart-quantity', item.quantity.toString())

        // Update the quantity selector's internal state
        if (
          'updateCartQuantity' in selector &&
          typeof selector.updateCartQuantity === 'function'
        ) {
          selector.updateCartQuantity()
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    const cartQuantitySelectors = document.querySelectorAll(
      'cart-quantity-selector-component'
    )
    for (const selector of cartQuantitySelectors) {
      if (
        'updateButtonStates' in selector &&
        typeof selector.updateButtonStates === 'function'
      ) {
        selector.updateButtonStates()
      }
    }
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset

    if (!sectionId) throw new Error('Section id missing')

    return sectionId
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent)
}
